'use client';

/**
 * Login Page
 *
 * Handles authentication for charity admin users
 */

import { useState, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormData = z.infer<typeof formSchema>;

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Seeded once from the URL (middleware error / registration success
  // message) via lazy initialisers — no effect, no second render.
  const [error, setError] = useState<string | null>(() => {
    const errorParam = searchParams.get('error');
    if (!errorParam) return null;
    return errorParam === 'unauthorized'
      ? 'You do not have access to this charity'
      : 'An error occurred during login';
  });
  const [message] = useState<string | null>(() => searchParams.get('message'));
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        // Account lockout (lib/auth/lockout.ts) — the one credentials error
        // that is deliberately distinguishable, so the user knows the right
        // password will be refused and that a reset unlocks immediately.
        if (result.code === 'account_locked') {
          setError('account_locked');
          setIsLoading(false);
          return;
        }
        // Check if rate limited (error message contains rate limit text)
        if (result.error.toLowerCase().includes('too many') || result.error.includes('rate limit')) {
          setError('Too many login attempts. Please try again in 15 minutes.');
          setIsLoading(false);
          return;
        }
        setError('Invalid email or password');
        setIsLoading(false);
        return;
      }

      // Check HTTP status for rate limiting (signIn may return ok:false with status)
      if (result?.status === 429) {
        setError('Too many login attempts. Please try again in 15 minutes.');
        setIsLoading(false);
        return;
      }

      // Get callback URL or redirect to dashboard
      const callbackUrl = searchParams.get('callbackUrl') || '/dashboard';
      router.push(callbackUrl);
      router.refresh();
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Login to TrusteeKit</CardTitle>
          <CardDescription>
            Enter your credentials to access your charity dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  {message && (
                    <Alert>
                      <AlertDescription>{message}</AlertDescription>
                    </Alert>
                  )}

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        {error === 'account_locked' ? (
                          <>
                            This account is temporarily locked after too many failed sign-in
                            attempts. Try again in 15 minutes, or{' '}
                            <Link href="/forgot-password" className="underline font-medium">
                              reset your password
                            </Link>{' '}
                            to unlock it now.
                          </>
                        ) : (
                          error
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="admin@yourcharity.org"
                            autoComplete="email"
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            {...field}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>

                  <p className="text-sm text-center text-gray-600">
                    <a href="/forgot-password" className="text-blue-600 hover:underline">
                      Forgot your password?
                    </a>
                  </p>

                  <p className="text-sm text-center text-gray-600">
                    Don&apos;t have an account?{' '}
                    <a href="/signup" className="text-blue-600 hover:underline">
                      Sign up
                    </a>
                  </p>

                  <p className="text-sm text-center text-gray-600">
                    Need help?{' '}
                    <a
                      href="mailto:%22TrusteeKit%20Help%22%20%3Cinfo%40trusteekit.co.uk%3E?subject=Help%20with%20TrusteeKit"
                      className="text-blue-600 hover:underline"
                    >
                      info@trusteekit.co.uk
                    </a>
                  </p>
          </form>
        </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
